import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { 
  Button, 
  Card, 
  CardBody, 
  CardTitle, 
  Col, 
  Container, 
  Row, 
  Modal, 
  ModalHeader, 
  ModalBody, 
  ModalFooter,
  Table,
  Label
} from "reactstrap";
import * as Yup from "yup";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import ErrorAlert from "../../components/Common/Error/ErrorAlert";
import { generatePresignedUrl } from "../../store/common/actions";
import { createTicket, fetchTickets } from "../../store/ticket/actions";
import CommonForm from "../../components/Common/CommonForm";
import { getAllForms } from "../../store/auth/login/actions";
import CreatTicketMap from "../../components/Common/Map/creatTicketMap";
import ForestFireModal from "./ForestFireModel";
import { toast } from "react-toastify";
import { tickets, ticketsManagement } from "../../helpers/backend_helper";

export default function CreateTicket() {
  const [formConfig, setFormConfig] = useState([]);
  const [selectedIncidentType, setSelectedIncidentType] = useState("");
  const [formData, setFormData] = useState({});
  const [assignedUser, setAssignedUser] = useState(null);
  const [forestFireModalOpen, setForestFireModalOpen] = useState(false);
  const [compData, setCompData] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [storedIncidentsResponse, setStoredIncidentsResponse] = useState([]);
  const channel = new BroadcastChannel("dashboardChannel");

  const { error = null } = useSelector((state) => state.ticketReducer || {});

  const incidentTypes = JSON.parse(
    localStorage.getItem("incidentTypes") || "[]"
  );

  const initializeFormValues = (fields) =>
    fields.reduce((acc, field) => {
      acc[field.inputName] = field.type === "file" ? [] : "";
      return acc;
    }, {});

  useEffect(() => {
    dispatch(getAllForms());
  }, []);

  useEffect(() => {
    if (formConfig.length > 0) {
      setFormData((prev) => ({
        ...initializeFormValues(formConfig),
        ...prev,
      }));
    }
  }, [formConfig]);

  useEffect(() => {
    const storedFormResponse = JSON.parse(localStorage.getItem("formResponse"));
    const incidents = JSON.parse(localStorage.getItem("incidentTypes") || "[]");
    setStoredIncidentsResponse(incidents);
    if (storedFormResponse) {
      const ticketCreationForm = storedFormResponse.find(
        (form) => form.formId === "ticket_creation_form"
      );
      const ticketCommonForm = storedFormResponse.find(
        (form) => form.formId === "ticket_common_form"
      );

      // Extract nested fields for selected incident type
      const incidentTypeFields =
        ticketCreationForm?.fields[selectedIncidentType] || [];

      // Combine incident-specific fields and common ticket fields
      const combinedFields = [
        ...(Array.isArray(ticketCommonForm?.fields)
          ? ticketCommonForm.fields
          : []),
        ...incidentTypeFields,
      ];

      setFormConfig(combinedFields);
    }
    
    // Load comps.json when forest fire incident type is selected
    if (selectedIncidentType === "fire") {
      fetch('/Comps.json')
        .then(response => response.json())
        .then(data => {
          setCompData(data);
        })
        .catch(error => {
          console.error('Error loading comps.json:', error);
        });
    }
  }, [selectedIncidentType]);

  const handleMapLocationSelect = ({ lat, lng }) => {
    if (lat === undefined || lng === undefined) {
      console.warn("Invalid location data received:", { lat, lng });
      return;
    }
    const locationValue = `${lat}, ${lng}`;
    setFormData((prev) => ({
      ...prev,
      incidentLocation: locationValue,
      latitude: lat || 0,
      longitude: lng || 0,
    }));
  };

  const generateValidationSchema = (fields) => {
    return Yup.object(
      fields.reduce((schema, field) => {
        if (field.required) {
          schema[field.inputName] = Yup.string().required(
            `${field.label} is required`
          );
        }
        return schema;
      }, {})
    );
  };

  const handleSubmit = async (values) => {
    try {
     
      const selectedIncident = incidentTypes.find(
        (incident) => incident.id === selectedIncidentType
      );

      if (!selectedIncident) {
        console.error("Invalid or missing incident type in localStorage");
        return;
      }
      const incidentTypeId = selectedIncident.incidentTypeId;

      const fileFields = Object.keys(values).filter(
        (key) => Array.isArray(values[key]) && values[key]?.[0] instanceof File
      );

      const files = fileFields.flatMap((key) =>
        Array.from(values[key]).map((file) => ({
          name: file.name,
          file,
          fieldName: key,
        }))
      );
      const media = [];

      files.forEach(({ name, file, fieldName }) => {
        const filePath = `ticket/media/${fieldName}/${name}`;
        dispatch(generatePresignedUrl({ fileName: filePath, file }));
        media.push(filePath); // Add to media array
      })

      const payload = {
        act: "ticket",
        subact: "create",
        info: {
          ...values,
        incidentTypeId,
        media,
        incidentLocation: values.latitude && values.longitude
  ? {
      lat: values.latitude,
      long: values.longitude,
    }
  : {
      lat: parseFloat(values.incidentLocation?.split(',')[0] || '0'),
      long: parseFloat(values.incidentLocation?.split(',')[1] || '0'),
    }}

      };
      delete payload.latitude;
      delete payload.longitude;
      const response = await tickets(payload);
  
      if (response.message.includes("success")) {
        channel.postMessage("update_dashboard");

        toast.success(response?.message, {
          position: "top-right",
          autoClose: 3000,
        });
        navigate("/manage-ticket");
      }

    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const createTicketFromLocation = async (location) => {
    try {
      const selectedIncident = incidentTypes.find(
        (incident) => incident.id === selectedIncidentType
      );

      if (!selectedIncident) {
        console.error("Invalid or missing incident type");
        return;
      }

      const incidentTypeId = selectedIncident.incidentTypeId;
      const {divisionId,coordinates,status,...otherdata} = location.compData;
      const payload = {
        act: "ticket",
        subact: "createFire",
        info: {
          date: location.date,
          multiLocations: location.count > 1 ? location.multiLocations : '',
          incidentLocation: {
            lat: location.lat,
            long: location.lng
          },
          incidentTypeId,
          fireCount: location?.count ?? 1,
          time: location.time,
          ...(location.compData && { 
            divisionId: String(divisionId),
            ...otherdata 
          })
        },
      };

      const response = await ticketsManagement(payload);
      if (response?.message) {
        channel.postMessage("update_dashboard");
        toast.success(response?.message);
      }
    } catch (error) {
      console.error("Error creating ticket:", error);
      toast.error("Failed to create ticket");
    }
  };

  const handleIncidentTypeChange = (e) => {
    setSelectedIncidentType(e.target.value);
  };
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Row>
            <Col lg={6}>
              <Card>
                <CardBody>
                  <div className="mb-3 d-flex align-items-center">
                    <div className="flex-grow-1">
                      <label htmlFor="incidentType">
                        घटनेचा प्रकार (Type of Incident)
                      </label>
                      <select
                        id="incidentType"
                        className="form-control"
                        value={selectedIncidentType}
                        onChange={handleIncidentTypeChange}
                      >
                        <option value="">-- Select --</option>
                        {storedIncidentsResponse.map((incident) => (
                          <option key={incident.id} value={incident.id}>
                            {incident.incidentName}
                          </option>
                        ))}
                      </select>
                    </div>
                    {selectedIncidentType === "fire" && (
                      <div className="ms-2 d-flex align-self-end">
                        <Button 
                          color="primary" 
                          className="btn" 
                          onClick={() => setForestFireModalOpen(true)}
                        >
                          Create through KML
                        </Button>
                      </div>
                    )}
                  </div>

                  {selectedIncidentType && (
                    <CommonForm
                      formConfig={formConfig}
                      initialValues={formData}
                      setFormData={setFormData}
                      validationSchema={generateValidationSchema(formConfig)}
                      disabled={false}
                      onSubmit={handleSubmit}
                      submitText="Create Ticket"
                      assignedUser={assignedUser}
                      setAssignedUser={setAssignedUser}
                    />
                  )}


                  <br />
                  <ErrorAlert error={error} />
                </CardBody>
              </Card>
            </Col>
            <Col lg={6}>
              <Card>
                <CardBody>
                  <CardTitle>पता (Address)</CardTitle>
                  <CreatTicketMap 
                    SelectedLocation={handleMapLocationSelect} 
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Forest Fire Data Modal with Map and Table */}
      {forestFireModalOpen && <ForestFireModal
        isOpen={forestFireModalOpen}
        toggle={() => setForestFireModalOpen(!forestFireModalOpen)}
        compData={compData}
        createTicketFromLocation={createTicketFromLocation}
      />}
    </React.Fragment>
  );
}